/* =========================================================
   FILE: cn-product-rarity-badges.js
   CARDCOMPLEX Produktkarten- und Raritäts-Runtime
========================================================= */

(() => {
  'use strict';

  if (window.__cnProductRarityBadgesV51Loaded) return;
  window.__cnProductRarityBadgesV51Loaded = true;

  const META_SELECTOR =
    '.ygo-cardmeta__rarity, .ygo-cardmeta__rarity--inline';

  const TEXT_SELECTOR =
    '.ygo-cardmeta__rarity-text';

  const ICON_SELECTOR =
    '.ygo-cardmeta__rarity-icon';

  const TITLE_SELECTOR = [
    '.card-wrapper.product-card-wrapper .card__heading a',
    '.ygo-theme-deck-row .card__heading a',
    '.ygo-theme-deck__slide .card__heading a'
  ].join(',');

  const CARD_TABLE_ICON_SELECTOR =
    'product-info .ygo-icontext__icon--rarity-code';

  const BADGE_SELECTOR = [
    '.cn-rarity-badge',
    '.cn-rarities__badge--code',
    '.ygo-rarity-badge--code',
    '.cn-product-rarity-badge',
    '.ygo-card-product-rarity-code'
  ].join(',');

  const PRODUCT_CARD_SELECTOR = [
    '.card-wrapper.product-card-wrapper',
    '.ygo-theme-deck__slide'
  ].join(',');

  const CLICK_IGNORE_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'textarea',
    'select',
    'summary',
    'details',
    'label',
    '[role="button"]',
    '.button',
    '.quick-add',
    '.quick-add__submit',
    '.product-form',
    '.quantity',
    META_SELECTOR,
    TEXT_SELECTOR,
    '.cn-rarity-badge'
  ].join(',');

  const CODE_ALIASES = new Map([
    ['C', 'C'],
    ['COMMON', 'C'],
    ['R', 'R'],
    ['RARE', 'R'],
    ['GLR', 'GLR'],
    ['GOLD LETTER RARE', 'GLR'],
    ['SR', 'SUR'],
    ['SUR', 'SUR'],
    ['SUPER RARE', 'SUR'],
    ['UR', 'UR'],
    ['ULTRA RARE', 'UR'],
    ['SCR', 'SER'],
    ['SER', 'SER'],
    ['SECRET RARE', 'SER'],
    ['UTR', 'UTR'],
    ['ULTIMATE RARE', 'UTR'],
    ['GHR', 'GHR'],
    ['GHOST RARE', 'GHR'],
    ['PSCR', 'PSCR'],
    ['PLATINUM SECRET RARE', 'PSCR'],
    ['GUR', 'GUR'],
    ['GOLD RARE', 'GUR'],
    ['SIUR', 'SIUR'],
    ['SILVER ULTRA RARE', 'SIUR'],
    ['BLUR', 'BLUR'],
    ['BLUE ULTRA RARE', 'BLUR'],
    ['REUR', 'REUR'],
    ['RED ULTRA RARE', 'REUR'],
    ['PUUR', 'PUUR'],
    ['PURPLE ULTRA RARE', 'PUUR'],
    ['GRUR', 'GRUR'],
    ['GREEN ULTRA RARE', 'GRUR'],
    ['GGR', 'GGR'],
    ['GOLD GHOST RARE', 'GGR'],
    ['PUR', 'PUR'],
    ['ULTRA PARALLEL RARE', 'PUR'],
    ['PGR', 'PGR'],
    ['PREMIUM GOLD RARE', 'PGR'],
    ['QCSR', '25'],
    ['QUARTER CENTURY SECRET RARE', '25'],
    ['25', '25'],
    ['25TH', '25'],
    ['GSR', 'GSR'],
    ['GOLD SECRET RARE', 'GSR'],
    ['SFR', 'SFR'],
    ['STARFOIL RARE', 'SFR'],
    ['SP', 'SP'],
    ['SPECIAL', 'SP']
  ]);

  let isUpgrading = false;
  let mutationTimer = 0;
  let scrollFrame = 0;
  let scrollResizeObserver = null;
  let scrollRuntimeActivated = false;
  let cardPointerStart = null;

  const scrollElements = new Set();
  const dirtyScrollElements = new Set();
  const scrollElementState = new WeakMap();

  function applyScrollState(element, nextState) {
    const previousState = scrollElementState.get(element);

    if (
      previousState &&
      previousState.overflow === nextState.overflow &&
      previousState.atStart === nextState.atStart &&
      previousState.atEnd === nextState.atEnd
    ) {
      return;
    }

    scrollElementState.set(element, nextState);

    element.classList.toggle(
      'cn-rarity-scroll-overflow',
      nextState.overflow
    );

    element.classList.toggle(
      'is-cn-scrollable',
      nextState.overflow
    );

    element.classList.toggle(
      'is-at-start',
      nextState.overflow && nextState.atStart
    );

    element.classList.toggle(
      'is-at-end',
      nextState.overflow && nextState.atEnd
    );
  }

  function flushScrollUpdates() {
    scrollFrame = 0;
    const measurements = [];

    dirtyScrollElements.forEach((element) => {
      dirtyScrollElements.delete(element);

      if (!element?.isConnected) {
        scrollElements.delete(element);
        scrollElementState.delete(element);
        scrollResizeObserver?.unobserve(element);
        return;
      }

      /*
        Zuerst alle Layoutwerte lesen und erst anschließend Klassen
        schreiben. So bleibt die wiederhergestellte Scrollfunktion
        deutlich reflow-ärmer als die frühere Einzelmessung pro Element.
      */
      const clientWidth = element.clientWidth;
      if (clientWidth <= 0) return;

      const scrollWidth = element.scrollWidth;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const scrollLeft = Math.max(
        0,
        Math.min(maxScroll, element.scrollLeft)
      );

      const overflow = maxScroll > 1;

      measurements.push({
        element,
        state: {
          overflow,
          atStart: !overflow || scrollLeft <= 1,
          atEnd: !overflow || scrollLeft >= maxScroll - 1
        }
      });
    });

    measurements.forEach(({ element, state }) => {
      applyScrollState(element, state);
    });
  }

  function scheduleScrollUpdate(element) {
    if (element) dirtyScrollElements.add(element);
    if (scrollFrame) return;

    scrollFrame = window.requestAnimationFrame(
      flushScrollUpdates
    );
  }

  function scheduleAllScrollUpdates() {
    scrollElements.forEach((element) => {
      dirtyScrollElements.add(element);
    });

    if (scrollFrame) return;

    scrollFrame = window.requestAnimationFrame(
      flushScrollUpdates
    );
  }

  function scheduleSettledScrollUpdates() {
    const run = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(scheduleAllScrollUpdates);
      });
    };

    if (document.readyState === 'complete') {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 800 });
      } else {
        window.setTimeout(run, 120);
      }
      return;
    }

    window.addEventListener(
      'load',
      () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(run, { timeout: 800 });
        } else {
          window.setTimeout(run, 120);
        }
      },
      { once: true }
    );
  }

  function hasScrollableOverflow(element) {
    const cached = scrollElementState.get(element);

    if (cached) {
      return cached.overflow;
    }

    /*
      Vor der ersten Nutzerinteraktion wird absichtlich nicht gemessen.
      Ein unbekannter Zustand wird zunächst als potenziell scrollbar
      behandelt. Die echte Messung folgt erst nach der Interaktion.
    */
    activateScrollRuntime();
    scheduleScrollUpdate(element);
    return true;
  }

  function markDragged(element) {
    element.dataset.cnDidDrag = 'true';

    const link = element.closest('a');

    if (link) {
      link.dataset.cnDidDrag = 'true';
    }
  }

  function clearDragged(element) {
    window.setTimeout(() => {
      delete element.dataset.cnDidDrag;

      const link = element.closest('a');

      if (link) {
        delete link.dataset.cnDidDrag;
      }
    }, 100);
  }

  function bindDragScroll(element) {
    if (!element || element.dataset.cnDragScrollBound === 'true') {
      return;
    }

    element.dataset.cnDragScrollBound = 'true';

    let active = false;
    let horizontalDrag = false;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;

    element.addEventListener('pointerdown', (event) => {
      if (!hasScrollableOverflow(element)) return;
      if (event.button !== undefined && event.button !== 0) return;

      active = true;
      horizontalDrag = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = element.scrollLeft;

      element.classList.add('is-cn-drag-ready');
    });

    element.addEventListener(
      'pointermove',
      (event) => {
        if (!active || event.pointerId !== pointerId) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        const absoluteX = Math.abs(deltaX);
        const absoluteY = Math.abs(deltaY);

        if (!horizontalDrag) {
          if (
            absoluteY > 8 &&
            absoluteY > absoluteX * 1.15
          ) {
            active = false;
            element.classList.remove('is-cn-drag-ready');
            return;
          }

          if (
            absoluteX > 6 &&
            absoluteX > absoluteY * 1.05
          ) {
            horizontalDrag = true;
            markDragged(element);

            element.classList.add('is-cn-dragging');

            try {
              element.setPointerCapture(event.pointerId);
            } catch (_error) {}
          }
        }

        if (!horizontalDrag) return;

        element.scrollLeft =
          startScrollLeft - deltaX;

        event.preventDefault();
        scheduleScrollUpdate(element);
      },
      { passive: false }
    );

    const finishDrag = (event) => {
      if (!active && !horizontalDrag) return;

      active = false;

      element.classList.remove(
        'is-cn-drag-ready',
        'is-cn-dragging'
      );

      try {
        if (event?.pointerId != null) {
          element.releasePointerCapture(event.pointerId);
        }
      } catch (_error) {}

      clearDragged(element);
      horizontalDrag = false;
      pointerId = null;
    };

    element.addEventListener('pointerup', finishDrag);
    element.addEventListener('pointercancel', finishDrag);
    element.addEventListener(
      'lostpointercapture',
      finishDrag
    );

    element.addEventListener(
      'click',
      (event) => {
        if (element.dataset.cnDidDrag !== 'true') return;

        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    element.addEventListener(
      'wheel',
      (event) => {
        if (!hasScrollableOverflow(element)) return;

        const horizontalGesture =
          Math.abs(event.deltaX) > Math.abs(event.deltaY);

        const shiftWheel =
          event.shiftKey && Math.abs(event.deltaY) > 0;

        if (!horizontalGesture && !shiftWheel) return;

        const delta = horizontalGesture
          ? event.deltaX
          : event.deltaY;

        const previousScrollLeft = element.scrollLeft;
        element.scrollLeft += delta;

        if (element.scrollLeft !== previousScrollLeft) {
          event.preventDefault();
          scheduleScrollUpdate(element);
        }
      },
      { passive: false }
    );
  }

  function bindScrollableText(element, type) {
    if (!element) return;

    element.classList.add(
      'cn-rarity-scroll-text',
      `cn-rarity-scroll-text--${type}`
    );

    scrollElements.add(element);

    if (element.dataset.cnScrollStateBound !== 'true') {
      element.dataset.cnScrollStateBound = 'true';

      element.addEventListener(
        'scroll',
        () => scheduleScrollUpdate(element),
        { passive: true }
      );
    }

    if (scrollRuntimeActivated) {
      scrollResizeObserver?.observe(element);
    }

    bindDragScroll(element);
  }

  function startScrollResizeObserver() {
    if (
      scrollResizeObserver ||
      !('ResizeObserver' in window)
    ) {
      return;
    }

    scrollResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        scheduleScrollUpdate(entry.target);
      });
    });

    scrollElements.forEach((element) => {
      scrollResizeObserver.observe(element);
    });
  }

  function activateScrollRuntime() {
    if (scrollRuntimeActivated) return;

    scrollRuntimeActivated = true;
    startScrollResizeObserver();
    scheduleSettledScrollUpdates();
  }

  function bindScrollRuntimeActivation() {
    const activate = () => activateScrollRuntime();

    document.addEventListener('pointerdown', activate, {
      once: true,
      passive: true,
      capture: true
    });

    document.addEventListener('touchstart', activate, {
      once: true,
      passive: true,
      capture: true
    });

    document.addEventListener('wheel', activate, {
      once: true,
      passive: true,
      capture: true
    });

    document.addEventListener('keydown', activate, {
      once: true,
      capture: true
    });
  }

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeKey(value) {
    return normalizeText(value)
      .toUpperCase()
      .replace(/\./g, '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getCode(value) {
    const key = normalizeKey(value);
    if (!key) return '';

    const direct = CODE_ALIASES.get(key);
    if (direct) return direct;

    const patterns = [
      ['QUARTER CENTURY SECRET', '25'],
      ['PLATINUM SECRET', 'PSCR'],
      ['GOLD SECRET', 'GSR'],
      ['GOLD GHOST', 'GGR'],
      ['GOLD LETTER', 'GLR'],
      ['PREMIUM GOLD', 'PGR'],
      ['ULTRA PARALLEL', 'PUR'],
      ['SILVER ULTRA', 'SIUR'],
      ['BLUE ULTRA', 'BLUR'],
      ['RED ULTRA', 'REUR'],
      ['PURPLE ULTRA', 'PUUR'],
      ['GREEN ULTRA', 'GRUR'],
      ['ULTIMATE', 'UTR'],
      ['GHOST', 'GHR'],
      ['SECRET', 'SER'],
      ['SUPER', 'SUR'],
      ['ULTRA', 'UR'],
      ['COMMON', 'C']
    ];

    for (const [needle, code] of patterns) {
      if (key.includes(needle)) return code;
    }

    if (key === 'RARE' || key.endsWith(' RARE')) return 'R';
    return '';
  }

  function setBadgeCode(badge, code, label = '') {
    const normalizedCode = getCode(code) || code;
    if (!normalizedCode) return;

    if (badge.dataset.cnRarityBadge !== normalizedCode) {
      badge.dataset.cnRarityBadge = normalizedCode;
    }

    if (badge.dataset.ygoRarityBadge !== normalizedCode) {
      badge.dataset.ygoRarityBadge = normalizedCode;
    }

    if (badge.textContent !== normalizedCode) {
      badge.textContent = normalizedCode;
    }

    if (label && !badge.hasAttribute('aria-label')) {
      badge.setAttribute('aria-label', `${label} Rarität`);
    }
  }

  function createBadge(code, label) {
    const badge = document.createElement('span');
    badge.className =
      'cn-product-rarity-badge ygo-card-product-rarity-code';
    badge.setAttribute('aria-hidden', 'true');
    setBadgeCode(badge, code, label);
    return badge;
  }

  function ensureMetaText(meta) {
    const existing = meta.querySelector(TEXT_SELECTOR);
    if (existing) return existing;

    const textNodes = Array.from(meta.childNodes).filter(
      (node) =>
        node.nodeType === Node.TEXT_NODE &&
        normalizeText(node.textContent)
    );

    if (!textNodes.length) return null;

    const label = normalizeText(
      textNodes.map((node) => node.textContent).join(' ')
    );

    if (!label) return null;

    const text = document.createElement('span');
    text.className = 'ygo-cardmeta__rarity-text';
    text.textContent = label;

    const icon = meta.querySelector(
      `${ICON_SELECTOR}, ${BADGE_SELECTOR}`
    );

    if (icon) {
      meta.insertBefore(text, icon);
    } else {
      meta.appendChild(text);
    }

    textNodes.forEach((node) => node.remove());
    return text;
  }

  function upgradeMeta(meta) {
    if (!meta || meta.dataset.cnRarityCodeReady === 'true') {
      return;
    }

    const text = ensureMetaText(meta);

    if (text) {
      bindScrollableText(text, 'rarity');
    }

    const label = normalizeText(
      text?.textContent || meta.textContent
    );

    const code = getCode(label);
    if (!code) return;

    let iconWrap = meta.querySelector(ICON_SELECTOR);

    if (!iconWrap) {
      iconWrap = document.createElement('span');
      iconWrap.className = 'ygo-cardmeta__rarity-icon';

      if (text) {
        text.after(iconWrap);
      } else {
        meta.appendChild(iconWrap);
      }
    }

    iconWrap.classList.add(
      'ygo-cardmeta__rarity-icon--code'
    );

    let badge = iconWrap.querySelector(BADGE_SELECTOR);

    if (!badge) {
      badge = createBadge(code, label);
      iconWrap.replaceChildren(badge);
    } else {
      setBadgeCode(badge, code, label);
      iconWrap
        .querySelectorAll('img, svg')
        .forEach((node) => node.remove());
    }

    Array.from(meta.children).forEach((child) => {
      if (
        child !== iconWrap &&
        child.matches(BADGE_SELECTOR)
      ) {
        child.remove();
      }
    });

    meta.dataset.cnRarityCodeReady = 'true';
  }

  function ensureTitleText(link) {
    if (!link) return;

    let text = link.querySelector(
      '.cn-product-card-title-scroll'
    );

    if (!text) {
      const label = normalizeText(link.textContent);
      if (!label) return;

      text = document.createElement('span');
      text.className = 'cn-product-card-title-scroll';
      text.textContent = label;

      link.replaceChildren(text);

      if (!link.hasAttribute('aria-label')) {
        link.setAttribute('aria-label', label);
      }
    }

    link.classList.add(
      'cn-product-card-title-scroll-link'
    );

    link.dataset.cnProductTitleReady = 'true';

    bindScrollableText(text, 'title');
  }

  function upgradeCardTable(scope) {
    scope
      .querySelectorAll(CARD_TABLE_ICON_SELECTOR)
      .forEach((icon) => {
        const row = icon.closest('.ygo-icontext');
        if (!row) return;

        row.classList.add(
          'ygo-icontext--rarity',
          'cn-rarity-inline-row'
        );

        const text = row.querySelector(
          '.ygo-icontext__text'
        );

        if (text) {
          bindScrollableText(text, 'rarity');
        }
      });
  }

  function upgrade(scope = document) {
    if (!scope || isUpgrading) return;

    isUpgrading = true;

    try {
      scope
        .querySelectorAll(META_SELECTOR)
        .forEach(upgradeMeta);

      scope
        .querySelectorAll(TITLE_SELECTOR)
        .forEach(ensureTitleText);

      upgradeCardTable(scope);
    } finally {
      isUpgrading = false;
    }
  }

  function nodeNeedsUpgrade(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches(META_SELECTOR) ||
      node.matches(TITLE_SELECTOR) ||
      node.matches(CARD_TABLE_ICON_SELECTOR) ||
      Boolean(
        node.querySelector(
          `${META_SELECTOR}, ${TITLE_SELECTOR}, ${CARD_TABLE_ICON_SELECTOR}`
        )
      )
    );
  }

  function startMutationObserver() {
    if (!('MutationObserver' in window)) return;

    const root =
      document.querySelector('#MainContent') ||
      document.body;

    if (!root) return;

    const observer = new MutationObserver((mutations) => {
      if (isUpgrading) return;

      const scopes = new Set();

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (nodeNeedsUpgrade(node)) {
            scopes.add(node);
          }
        });
      });

      if (!scopes.size) return;

      window.clearTimeout(mutationTimer);

      mutationTimer = window.setTimeout(() => {
        scopes.forEach((scope) => upgrade(scope));

        if (scrollRuntimeActivated) {
          scheduleSettledScrollUpdates();
        }
      }, 50);
    });

    observer.observe(root, {
      childList: true,
      subtree: true
    });
  }

  function getProductCard(target) {
    return target instanceof Element
      ? target.closest(PRODUCT_CARD_SELECTOR)
      : null;
  }

  function getProductCardLink(card) {
    return (
      card?.querySelector('.card__heading a[href]') ||
      card?.querySelector(
        'a.full-unstyled-link[href*="/products/"]'
      ) ||
      card?.querySelector('a[href*="/products/"]') ||
      null
    );
  }

  function shouldIgnoreCardClick(event) {
    return (
      !event ||
      event.defaultPrevented ||
      !(event.target instanceof Element) ||
      Boolean(event.target.closest(CLICK_IGNORE_SELECTOR))
    );
  }

  function bindProductCardNavigation() {
    const html = document.documentElement;

    if (
      html.dataset.cnProductCardClickV51 === 'true'
    ) {
      return;
    }

    html.dataset.cnProductCardClickV51 = 'true';

    document.addEventListener(
      'pointerdown',
      (event) => {
        const card = getProductCard(event.target);

        cardPointerStart = card
          ? {
              card,
              x: event.clientX,
              y: event.clientY
            }
          : null;
      },
      { passive: true }
    );

    document.addEventListener('click', (event) => {
      const card = getProductCard(event.target);

      if (
        !card ||
        shouldIgnoreCardClick(event)
      ) {
        return;
      }

      if (
        cardPointerStart?.card === card &&
        (
          Math.abs(event.clientX - cardPointerStart.x) > 8 ||
          Math.abs(event.clientY - cardPointerStart.y) > 8
        )
      ) {
        return;
      }

      const link = getProductCardLink(card);
      if (!link?.href) return;

      event.preventDefault();

      if (event.metaKey || event.ctrlKey) {
        window.open(link.href, '_blank', 'noopener');
      } else {
        window.location.href = link.href;
      }
    });

    document.addEventListener('auxclick', (event) => {
      if (event.button !== 1) return;

      const card = getProductCard(event.target);

      if (
        !card ||
        shouldIgnoreCardClick(event)
      ) {
        return;
      }

      const link = getProductCardLink(card);
      if (!link?.href) return;

      event.preventDefault();
      window.open(link.href, '_blank', 'noopener');
    });
  }

  function start() {
    upgrade(document);
    bindProductCardNavigation();
    bindScrollRuntimeActivation();
    startMutationObserver();

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        upgrade(event.target);

        if (scrollRuntimeActivated) {
          scheduleSettledScrollUpdates();
        }
      }
    );

    document.addEventListener(
      'shopify:section:select',
      (event) => {
        upgrade(event.target);

        if (scrollRuntimeActivated) {
          scheduleSettledScrollUpdates();
        }
      }
    );

    document.addEventListener(
      'facets:updated',
      () => {
        upgrade(
          document.querySelector('#MainContent') || document
        );

        if (scrollRuntimeActivated) {
          scheduleSettledScrollUpdates();
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
